import { Link } from "react-router-dom";
import { Mail, MapPin, ExternalLink } from "lucide-react";
import logoFooter from "@/assets/logo-footer.svg";
import indigenousFlags from "@/assets/indigenous-flags.png";
import { locations, getFullAddress } from "@/data/locations";
import { SocialIcons } from "@/components/shared/SocialIcons";

const footerLinks = {
  products: [{
    label: "Artificial Grass",
    href: "/artificial-grass"
  }, {
    label: "WPC Decking",
    href: "/wpc-decking"
  }, {
    label: "WPC External Cladding",
    href: "/wpc-external-cladding"
  }, {
    label: "Supreme Wet Wall Panels",
    href: "/pvc-wet-wall-panels"
  }, {
    label: "Indoor Cladding",
    href: "/indoor-cladding"
  }, {
    label: "Hybrid Flooring",
    href: "/hybrid-flooring"
  }, {
    label: "Hedges & Screens",
    href: "/hedges"
  }, {
    label: "Weed Mats",
    href: "/weed-mats"
  }, {
    label: "Garden Pebbles",
    href: "/garden-pebbles"
  }, {
    label: "Accessories",
    href: "/accessories"
  }],
  company: [{
    label: "About Us",
    href: "/about"
  }, {
    label: "Trade Enquiries",
    href: "/bulk-quote"
  }, {
    label: "Request a Quote",
    href: "/quote"
  }, {
    label: "Trade Quote",
    href: "/bulk-quote"
  }, {
    label: "Resources",
    href: "/resources"
  }, {
    label: "Contact",
    href: "/contact"
  }, {
    label: "Blog",
    href: "/blog"
  }, {
    label: "Our Picks",
    href: "/our-picks"
  }, {
    label: "Reviews",
    href: "/reviews"
  }, {
    label: "WPC Decking Warranty",
    href: "/wpc-decking-warranty"
  }, {
    label: "Artificial Grass Warranty",
    href: "/artificial-grass-warranty"
  }, {
    label: "Hybrid Flooring Warranty",
    href: "/hybrid-flooring-warranty"
  }],
  support: [{
    label: "Installation Guides",
    href: "/resources/guides"
  }, {
    label: "Area Calculator",
    href: "/resources/calculator"
  }, {
    label: "Connect an AI Assistant",
    href: "/connect"
  }, {
    label: "Blog",
    href: "/blog"
  }, {
    label: "Get a Quote",
    href: "/quote"
  }, {
    label: "Privacy Policy",
    href: "/privacy"
  }, {
    label: "Terms & Conditions",
    href: "/terms"
  }, {
    label: "Refund & Return Policy",
    href: "/refund-return-policy"
  }, {
    label: "Shipping & Delivery Policy",
    href: "/shipping-delivery-policy"
  }]
};
export function Footer() {
  return <footer className="bg-secondary text-secondary-foreground">
      {/* Main Footer */}
      <div className="container py-12 md:py-16">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-5">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block">
              <img src={logoFooter} alt="Supreme Turf" className="h-[52px] w-auto" />
            </Link>
            <p className="mt-4 max-w-sm text-sm text-secondary-foreground/80">
              Supplying premium building and landscaping materials to trade and homeowners 
              across QLD, NSW, VIC & SA.
            </p>
            <div className="mt-6 space-y-3">
              <a 
                href="mailto:admin@supremeturf.com.au" 
                className="flex items-center gap-3 text-sm hover:text-accent transition-colors"
              >
                <Mail className="h-4 w-4 flex-shrink-0" />
                admin@supremeturf.com.au
              </a>
              <div className="space-y-2 text-sm">
                {locations.map((location) => (
                  <div key={location.id} className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <Link 
                        to={`/locations/${location.id}`}
                        className="font-medium hover:text-accent transition-colors"
                      >
                        {location.name}
                      </Link>
                      <a 
                        href={location.gmbProfileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-secondary-foreground/70 hover:text-accent transition-colors text-xs"
                      >
                        {getFullAddress(location)}
                        <ExternalLink className="inline h-3 w-3 ml-1" />
                      </a>
                      <a 
                        href={`tel:${location.phone.replace(/\s/g, '')}`}
                        className="block hover:text-accent transition-colors"
                      >
                        {location.phone}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Social Links */}
            <div className="mt-6">
              <h3 className="mb-3 text-sm font-bold uppercase tracking-wider">Connect With Us</h3>
              <SocialIcons gap="gap-4" iconClassName="h-5 w-5" variant="dark" />
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wider">Products</h3>
            <ul className="space-y-2">
              {footerLinks.products.map(link => <li key={link.label}>
                  <Link to={link.href} className="text-sm text-secondary-foreground/80 hover:text-accent transition-colors">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wider">Company</h3>
            <ul className="space-y-2">
              {footerLinks.company.map(link => <li key={link.label}>
                  <Link to={link.href} className="text-sm text-secondary-foreground/80 hover:text-accent transition-colors">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wider">Support</h3>
            <ul className="space-y-2">
              {footerLinks.support.map(link => <li key={link.label}>
                  <Link to={link.href} className="text-sm text-secondary-foreground/80 hover:text-accent transition-colors">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>
        </div>
      </div>

      {/* Acknowledgement of Country */}
      <div className="acknowledgement-section border-t border-secondary-foreground/20">
        <div className="container py-8">
          <div className="flex flex-col items-center gap-4 text-center md:flex-row md:items-start md:text-left">
            {/* Aboriginal & Torres Strait Islander Flags */}
            <img src={indigenousFlags} alt="Aboriginal and Torres Strait Islander flags" className="h-16 w-auto flex-shrink-0" />
            <p className="max-w-3xl text-sm text-secondary-foreground/80">
              Supreme Turf acknowledges the Traditional Custodians of the lands on which we operate 
              across Australia. We pay our respects to Elders past, present, and emerging, and recognise 
              their continuing connection to land, waters, and community.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-secondary-foreground/20">
        <div className="container flex flex-col items-center justify-between gap-4 py-6 md:flex-row">
          <p className="text-sm text-secondary-foreground/60">
            © {new Date().getFullYear()} Supreme Turf. Operated by GOLDCOIN PACKAGING (AUSTRALIA) PTY. LTD. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-secondary-foreground/60">
            <Link to="/privacy" className="hover:text-accent transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-accent transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>;
}
